import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { TranslationKeys } from '@/core/i18n/translations';
import { COOKIE_CONSENT_STORAGE_KEY } from '@/shared/legal/consentStorage';

type Lang = 'en' | 'pl';

export interface CookieConsentBannerProps {
  language: Lang;
  t: TranslationKeys;
}

/**
 * Lightweight consent for cookies / local storage (auth session, language, guest counters).
 */
export function CookieConsentBanner({ language, t }: CookieConsentBannerProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const accept = useCallback(() => {
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'essential');
    } catch {
      /* ignore quota / private mode */
    }
    setVisible(false);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-label={language === 'pl' ? 'Zgoda na pliki cookie' : 'Cookie consent'}
      className="fixed bottom-0 inset-x-0 z-[100] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 pointer-events-none"
    >
      <div className="max-w-3xl mx-auto pointer-events-auto rounded-xl border border-slate-200 dark:border-slate-600 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-slate-700 dark:text-slate-200">
        <p className="flex-1 leading-snug">
          {t.cookieBannerMessage}{' '}
          <Link
            to="/privacy-policy"
            className="font-medium text-orange-800 underline underline-offset-2 decoration-orange-800 hover:text-orange-900 whitespace-nowrap dark:text-orange-200 dark:decoration-orange-200 dark:hover:text-orange-100"
          >
            {t.cookieBannerPrivacyLink}
          </Link>
        </p>
        <button
          type="button"
          data-testid="cookie-consent-accept"
          onClick={accept}
          className="shrink-0 rounded-lg bg-orange-700 hover:bg-orange-600 text-white font-semibold px-4 py-2 text-sm touch-manipulation"
        >
          {t.cookieBannerAccept}
        </button>
      </div>
    </div>
  );
}
